import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import SolidTimesCircleIcon from '../Icons/solid/SolidTimesCircleIcon'
import BodyText from '../Typography/BodyText'

const RemoveIcon = styled(SolidTimesCircleIcon)``

const Chip = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  border-radius: ${props => props.theme.borderRadius.round};
  position: relative;
  z-index: 0;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  gap: ${rem(8)};
  height: ${rem(28)};
  padding: 0 ${rem(8)};
  white-space: nowrap;

  &.variant-outline {
    background-color: transparent;
    color: ${props => props.theme.palette.neutral.one};
    border: 1px solid ${props => props.theme.palette.neutral.five};

    &:not(:disabled) {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.eight};
        border: 1px solid ${props => props.theme.palette.neutral.two};
      }

      &.error {
        border-color: ${props => props.theme.palette.messaging.critical.main};

        > ${RemoveIcon} {
          color: ${props => props.theme.palette.messaging.critical.main};
        }
      }

      .input-keyboard &:focus {
        outline: 2px solid  ${props => props.theme.palette.neutral.five};
        outline-offset: 2px;
      }
    }

    &:disabled {
      color: ${props => props.theme.palette.neutral.four};

      > ${RemoveIcon} {
        color: ${props => props.theme.palette.neutral.four};
      }
    }
  }

  &.variant-filled {
    background-color: ${props => props.theme.palette.neutral.eight};
    color: ${props => props.theme.palette.neutral.one};
    border: 1px solid ${props => props.theme.palette.neutral.five};

    &:not(:disabled) {
      &:hover {
        border: 1px solid ${props => props.theme.palette.neutral.two};
      }

      &.error {
        background-color: ${props => props.theme.palette.messaging.critical.light};

        > ${RemoveIcon} {
          color: ${props => props.theme.palette.messaging.critical.main};
        }
      }

      .input-keyboard &:focus {
        outline: 2px solid  ${props => props.theme.palette.neutral.five};
        outline-offset: 2px;
      }
    }

    &:disabled {
      background-color: ${props => props.theme.palette.neutral.four};
      color: ${props => props.theme.palette.neutral.eight};

      > ${RemoveIcon} {
        color: ${props => props.theme.palette.neutral.seven};
      }
    }
  }

  > svg, img {
    height: ${rem(16)};
    width: ${rem(16)};
  }
`

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  /** Determines the core visual representation of the chip */
  variant: 'filled' | 'outline';
  /** Whether or not the chip should show itself visually in an error state */
  error?: boolean;
  /** An icon element that will go at the start (often the 'left' side) of the chip */
  startIcon?: React.ReactElement;
}

function InputChip(props: Props) {
  const { children, className, variant, error, startIcon, ...buttonProps } = props

  return (<Chip
    {...buttonProps}
    className={cn(
      className,
      `variant-${variant}`,
      { error },
    )}
  >
    {startIcon}
    <BodyText variant="small">{children}</BodyText>
    <RemoveIcon colour="neutral-two" />
  </Chip>)
}

export default InputChip
